// Analiz / dağılım sekmelerinin ortak parçaları (docs/design-principles.md §4, §6):
// özel grafik tooltip'i, sıralı yatay çubuk listesi, grafiğin biçiminde yükleme
// iskeleti, kart içi boş durum ve sabit gövde yükseklikli bölüm kartı.
// İlk kullanıcı: Hastalar › Hasta Dağılımı.
import React from "react";
import { Box, Typography, Skeleton, Tooltip as MuiTooltip } from "@mui/material";
import { InsertChartOutlined as ChartIcon } from "@mui/icons-material";
import SectionCard from "../../molecules/SectionCard";
import EmptyState from "../../atoms/EmptyState";
import { COLORS, TYPO, CARD_PAD, dashboardSectionSx } from "../../../styles/uiConstants";

export const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);
export const nf = (n) => Number(n || 0).toLocaleString("tr-TR");

export const AXIS_TICK = { fontSize: 12, fill: COLORS.slate500 };
export const GRID = { stroke: COLORS.slate100, strokeDasharray: "0", vertical: false };

/** Grafik tooltip'i: değer önce, etiket sonra; seri anahtarı kısa çizgi. */
export function ChartTip({ active, payload, label, format }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: COLORS.white,
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
        px: 1.5,
        py: 1,
        minWidth: 120,
      }}
    >
      <Typography
        sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mb: 0.5 }}
      >
        {label}
      </Typography>
      {payload.map((p) => (
        <Box
          key={p.dataKey}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box
            sx={{
              width: 10,
              height: 2,
              borderRadius: 1,
              bgcolor: p.color || p.payload?.fill,
            }}
          />
          <Typography
            sx={{
              fontSize: TYPO.body,
              fontWeight: 700,
              color: COLORS.slate800,
            }}
          >
            {format ? format(p) : nf(Math.abs(p.value))}
          </Typography>
          {payload.length > 1 ? (
            <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
              {p.name}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

/** Sıralı yatay çubuk listesi — uzun etiketler (tanı, ilçe) için bar grafikten okunaklı. */
export function RankBarList({ items, color, total, emptyText, unit = "hasta" }) {
  const max = Math.max(0, ...items.map((i) => i.count));
  if (!items.length || !max) return <CardEmpty {...emptyText} />;
  return (
    // Kart yüksekliği sabit; sığmayan satırlar kart içinde kaydırılır
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        overscrollBehavior: "contain",
        pr: 1,
        mr: -1,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: COLORS.slate200,
          borderRadius: 3,
        },
      }}
    >
      {items.map((it) => (
        <MuiTooltip
          key={it.name}
          title={`${it.name}: ${nf(it.count)} ${unit}${total ? ` · %${pct(it.count, total)}` : ""}`}
          placement="top"
          arrow
        >
          <Box sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: TYPO.body,
                  color: COLORS.slate700,
                  minWidth: 0,
                }}
                noWrap
              >
                {it.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: TYPO.body,
                  fontWeight: 600,
                  color: COLORS.slate800,
                  fontVariantNumeric: "tabular-nums",
                  flexShrink: 0,
                }}
              >
                {nf(it.count)}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: COLORS.slate100,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${Math.max(3, (it.count / max) * 100)}%`,
                  borderRadius: 4,
                  bgcolor: it.muted ? COLORS.slate300 : color,
                }}
              />
            </Box>
          </Box>
        </MuiTooltip>
      ))}
    </Box>
  );
}

/** Kart gövdesi yüklenirken: grafiğin biçimini taklit eden iskelet (kart çerçevesi hemen görünür). */
const SK_HEIGHTS = [45, 70, 55, 85, 60, 95, 50, 75, 65, 80, 40, 70];
export function BodySkeleton({ kind }) {
  if (kind === "list") {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {[...Array(7)].map((_, i) => (
          <Box key={i}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Skeleton width={`${40 + ((i * 17) % 35)}%`} />
              <Skeleton width={20} />
            </Box>
            <Skeleton
              variant="rounded"
              height={8}
              width={`${95 - i * 10}%`}
              sx={{ borderRadius: 4 }}
            />
          </Box>
        ))}
      </Box>
    );
  }
  if (kind === "pyramid") {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Skeleton width={220} />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-around",
          }}
        >
          {[...Array(7)].map((_, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton width={36} />
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <Skeleton
                  variant="rounded"
                  height={16}
                  width={`${SK_HEIGHTS[i]}%`}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="rounded"
                  height={16}
                  width={`${SK_HEIGHTS[i + 3]}%`}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
  const bars = kind === "months" ? 12 : 8;
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-around",
        gap: 1,
        pb: 3,
        pl: 3,
      }}
    >
      {SK_HEIGHTS.slice(0, bars).map((h, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          width={18}
          height={`${h}%`}
          sx={{ borderRadius: "4px 4px 0 0" }}
        />
      ))}
    </Box>
  );
}

/** Kart içi boş durum — grafiğin yerini aynı yükseklikte doldurur. */
export function CardEmpty({ title, description, icon }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <EmptyState
        variant="illustrated-sm"
        icon={icon || <ChartIcon />}
        title={title}
        description={description}
        sx={{ py: 0 }}
      />
    </Box>
  );
}

/**
 * Bölüm kartı: gövde yüksekliği SABİT (`bodyHeight`) — yan yana kartlar içeriğe göre
 * birbirini uzatmaz; grafik/liste bu yüksekliği doldurur.
 */
export const AnalyticsCard = ({ title, subtitle, icon, action, bodyHeight, children }) => (
  <SectionCard
    title={title}
    subtitle={subtitle}
    icon={icon}
    action={action}
    sx={{ ...dashboardSectionSx, display: "flex", flexDirection: "column" }}
  >
    <Box
      sx={{
        p: CARD_PAD,
        height: bodyHeight,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {children}
    </Box>
  </SectionCard>
);
